/**
 * DelegateDashboard - Central hub for delegate event experience
 * Features: Registration status, sessions, accommodation, announcements, Bible widget
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { useAuthStore } from '@/lib/store';
import { cn } from '@/lib/utils';

// UI Components
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';

// Icons
import {
  CalendarCheck, Clock, MapPin, Bell, BookOpen, Home,
  CreditCard, Users, QrCode, ChevronRight, Sparkles, 
  CheckCircle2, AlertCircle, Ticket, Coffee, Utensils,
  FileText, MessageSquare, Calendar, Star
} from 'lucide-react';

// Bible Dashboard component (imported)
import BibleDashboard from '@/components/bible/BibleDashboard';

// ============== Registration Status Card ==============
const RegistrationStatusCard = ({ registration, event }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'pending': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'cancelled': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  return (
    <Card className="overflow-hidden">
      <div className="h-2 bg-gradient-to-r from-indigo-500 to-purple-500" />
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-semibold text-lg text-slate-900">My Registration</h3>
            <p className="text-sm text-slate-500 mt-1">{event?.name}</p>
          </div>
          <Badge className={getStatusColor(registration?.status)}>
            {registration?.status === 'confirmed' && <CheckCircle2 className="h-3 w-3 mr-1" />}
            {registration?.status || 'Pending'}
          </Badge>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
              <Ticket className="h-5 w-5 text-indigo-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Tier</p>
              <p className="font-medium text-slate-900">{registration?.tier?.name || 'Standard'}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <CreditCard className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-xs text-slate-500">Payment</p>
              <p className="font-medium text-slate-900">{registration?.payment_status || 'Pending'}</p>
            </div>
          </div>
        </div>

        {registration?.status !== 'confirmed' && (
          <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-100 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-amber-800">
              Complete your payment to confirm your registration.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ============== Upcoming Sessions Card ==============
const UpcomingSessionsCard = ({ sessions }) => {
  const navigate = useNavigate();

  if (!sessions || sessions.length === 0) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Calendar className="h-4 w-4 text-indigo-600" />
            My Schedule
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            <Clock className="h-8 w-8 text-slate-300 mx-auto mb-2" />
            <p className="text-sm text-slate-500">No sessions scheduled yet</p>
            <Button variant="link" size="sm" className="mt-2">
              Browse Sessions
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Calendar className="h-4 w-4 text-indigo-600" />
            Upcoming Sessions
          </CardTitle>
          <Button variant="ghost" size="sm" onClick={() => navigate('/delegate?tab=sessions')}>
            View All <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {sessions.slice(0, 3).map((session, idx) => (
          <div 
            key={session.id || idx}
            className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100 cursor-pointer transition-colors"
          >
            <div className="w-12 text-center">
              <div className="text-xs text-slate-500">
                {new Date(session.start_time).toLocaleDateString('en-US', { weekday: 'short' })}
              </div>
              <div className="font-bold text-indigo-600">
                {new Date(session.start_time).getDate()}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-slate-900 truncate">{session.title}</p>
              <p className="text-xs text-slate-500">
                {new Date(session.start_time).toLocaleTimeString('en-US', { 
                  hour: 'numeric', minute: '2-digit' 
                })} • {session.venue || 'TBA'}
              </p>
            </div>
            <ChevronRight className="h-4 w-4 text-slate-400" />
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

// ============== Accommodation Card ==============
const AccommodationCard = ({ accommodation }) => {
  if (!accommodation) {
    return null;
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Home className="h-4 w-4 text-indigo-600" />
          My Accommodation
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold">
            {accommodation.room_number || '—'}
          </div>
          <div>
            <p className="font-medium text-slate-900">{accommodation.building || 'Assigned Building'}</p>
            <p className="text-sm text-slate-500">
              Room {accommodation.room_number} • {accommodation.room_type || 'Standard'}
            </p>
          </div>
        </div>
        
        {accommodation.check_in && (
          <div className="mt-4 pt-4 border-t grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-slate-500">Check-in</p>
              <p className="font-medium">{new Date(accommodation.check_in).toLocaleDateString()}</p>
            </div>
            <div>
              <p className="text-slate-500">Check-out</p>
              <p className="font-medium">{new Date(accommodation.check_out).toLocaleDateString()}</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ============== Announcements Card ==============
const AnnouncementsCard = ({ announcements }) => {
  if (!announcements || announcements.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Bell className="h-4 w-4 text-indigo-600" />
          Announcements
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {announcements.slice(0, 3).map((announcement, idx) => (
          <div 
            key={announcement.id || idx}
            className={cn(
              "p-3 rounded-lg border-l-4",
              announcement.priority === 'high' 
                ? "bg-red-50 border-red-500" 
                : "bg-slate-50 border-indigo-500"
            )}
          >
            <div className="flex items-start justify-between gap-2">
              <h4 className="font-medium text-slate-900 text-sm">{announcement.title}</h4>
              {announcement.priority === 'high' && (
                <Badge variant="destructive" className="text-xs">Important</Badge>
              )}
            </div>
            <p className="text-xs text-slate-600 mt-1 line-clamp-2">{announcement.content}</p>
            <p className="text-xs text-slate-400 mt-2">
              {new Date(announcement.created_at).toLocaleDateString()}
            </p>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

// ============== Quick Actions ==============
const QuickActionsCard = ({ navigate, event }) => {
  const actions = [
    { icon: QrCode, label: 'My Badge', path: '/badge', color: 'bg-indigo-100 text-indigo-600' },
    { icon: Calendar, label: 'Schedule', path: '/delegate?tab=sessions', color: 'bg-emerald-100 text-emerald-600' },
    { icon: Users, label: 'Networking', path: '/networking', color: 'bg-purple-100 text-purple-600' },
    { icon: MapPin, label: 'Venue Map', path: '/map', color: 'bg-amber-100 text-amber-600' },
  ];

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-indigo-600" />
          Quick Actions
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {actions.map((action) => (
            <button
              key={action.label}
              onClick={() => navigate(action.path)}
              className="flex flex-col items-center gap-2 p-3 rounded-lg hover:bg-slate-50 transition-colors"
            >
              <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center", action.color)}>
                <action.icon className="h-5 w-5" />
              </div>
              <span className="text-xs text-slate-600">{action.label}</span>
            </button>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// ============== Event Info Header ==============
const EventInfoHeader = ({ event }) => {
  if (!event) return null;

  return (
    <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 rounded-2xl p-6 text-white shadow-xl">
      <div className="flex items-center gap-4">
        {event.logo_url ? (
          <Avatar className="h-16 w-16 border-2 border-white/30">
            <AvatarImage src={event.logo_url} />
            <AvatarFallback className="bg-white/20 text-white text-xl">
              {event.name?.charAt(0)}
            </AvatarFallback>
          </Avatar>
        ) : (
          <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
            <CalendarCheck className="h-8 w-8" />
          </div>
        )}
        <div>
          <h1 className="text-xl font-bold">{event.name}</h1>
          <div className="flex items-center gap-4 mt-1 text-white/80 text-sm">
            <span className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              {event.start_date && new Date(event.start_date).toLocaleDateString()}
            </span>
            <span className="flex items-center gap-1">
              <MapPin className="h-4 w-4" />
              {event.venue || 'Venue TBA'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============== Main Component ==============
const DelegateDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  // Fetch delegate's event data
  const { data: eventData } = useQuery({
    queryKey: ['delegate-event'],
    queryFn: () => api.get('/delegate/my-event').then(r => r.data).catch(() => null),
  });

  // Fetch delegate's registration
  const { data: registration } = useQuery({
    queryKey: ['delegate-registration'],
    queryFn: () => api.get('/delegate/my-registration').then(r => r.data).catch(() => null),
  });

  // Fetch upcoming sessions
  const { data: sessions = [] } = useQuery({
    queryKey: ['delegate-sessions'],
    queryFn: () => api.get('/delegate/my-sessions').then(r => r.data).catch(() => []),
  });

  // Fetch accommodation
  const { data: accommodation } = useQuery({
    queryKey: ['delegate-accommodation'],
    queryFn: () => api.get('/delegate/my-accommodation').then(r => r.data).catch(() => null),
  });

  // Fetch announcements
  const { data: announcements = [] } = useQuery({
    queryKey: ['event-announcements'],
    queryFn: () => api.get('/delegate/announcements').then(r => r.data).catch(() => []),
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6">
        {/* Welcome Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              Welcome back, {user?.name?.split(' ')[0] || 'Delegate'}!
            </h1>
            <p className="text-slate-500 mt-1">Here's your event overview</p>
          </div>
        </div>

        {/* Event Info */}
        <EventInfoHeader event={eventData} />

        {/* Registration Status */}
        <RegistrationStatusCard registration={registration} event={eventData} />

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Upcoming Sessions */}
          <UpcomingSessionsCard sessions={sessions} />

          {/* Quick Actions */}
          <QuickActionsCard navigate={navigate} event={eventData} />
        </div>

        {/* Accommodation */}
        <AccommodationCard accommodation={accommodation} />

        {/* Announcements */}
        <AnnouncementsCard announcements={announcements} />

        {/* Daily Bible Reading */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-indigo-600" />
              Daily Devotional
            </CardTitle>
          </CardHeader>
          <CardContent>
            <BibleDashboard embedded={true} />
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DelegateDashboard;
